import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
// @mui
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
// hooks
import {useBoolean} from 'packages/common/hooks/useBoolean';
import {useResponsive} from 'packages/common/hooks/useResponsive';
import {useAdminAccess} from 'packages/common/hooks/useAdminAccess';
import {useRouter} from 'packages/common/routes/hooks';
// components
import {useSettingsContext} from 'packages/common/components/settings';
import {AdminAccessProvider} from 'packages/common/contexts/admin-access-context';
import {AdminAccessBanner} from 'packages/common/components/admin-access-banner';
//
import Main from './main';
import NavMini from './nav-mini';
import NavVertical from './nav-vertical';
import NavHorizontal from './nav-horizontal';
import MobileHeader from './mobile-header';
import BottomNav from './bottom-nav';

// ----------------------------------------------------------------------

/**
 * Composant interne qui gère la logique de redirection et le rendu du layout admin
 * Doit être utilisé à l'intérieur de AdminAccessProvider pour accéder au contexte
 * @return {JSX.Element|null} The rendered layout or null during redirection
 */
function AdminLayoutContent({children}) {
  const settings = useSettingsContext();
  const lgUp = useResponsive('up', 'lg');
  const nav = useBoolean();
  const {isAdminAccessMode, isTransitioning} = useAdminAccess();
  const router = useRouter();

  // Rediriger vers /dashboard si en mode admin access (connecté sur un compte client)
  useEffect(() => {
    if (isAdminAccessMode && !isTransitioning) {
      router.replace('/dashboard');
    }
  }, [isAdminAccessMode, isTransitioning, router]);

  // Ne rien afficher pendant la redirection
  if (isAdminAccessMode && !isTransitioning) {
    return null;
  }

  const isHorizontal = settings.themeLayout === 'horizontal';
  const isMini = settings.themeLayout === 'mini';

  const renderNavMini = <NavMini />;
  const renderHorizontal = <NavHorizontal />;
  const renderNavVertical = <NavVertical openNav={nav.value} onCloseNav={nav.onFalse} />;

  if (isHorizontal) {
    return (
      <Box sx={{minHeight: {xs: '100dvh', lg: 1}, bgcolor: 'grey.100'}}>
        <AdminAccessBanner />

        {/* Header mobile uniquement */}
        {!lgUp && <MobileHeader onOpenNav={nav.onTrue} />}

        {lgUp ? renderHorizontal : renderNavVertical}
        <Main>{children}</Main>

        {/* Bottom nav mobile */}
        <BottomNav />
      </Box>
    );
  }

  if (isMini) {
    return (
      <>
        <AdminAccessBanner />

        {/* Header mobile uniquement */}
        {!lgUp && <MobileHeader onOpenNav={nav.onTrue} />}

        <Box
          sx={{
            minHeight: {xs: '100dvh', lg: 1},
            display: 'flex',
            flexDirection: {xs: 'column', lg: 'row'},
            bgcolor: 'grey.100',
          }}
        >
          {lgUp ? renderNavMini : renderNavVertical}
          <Main>{children}</Main>
        </Box>

        {/* Bottom nav mobile */}
        <BottomNav />
      </>
    );
  }

  return (
    <>
      <AdminAccessBanner />

      {/* Header mobile uniquement */}
      {!lgUp && <MobileHeader onOpenNav={nav.onTrue} />}

      <Box
        sx={{
          minHeight: {xs: '100dvh', lg: 1},
          display: 'flex',
          flexDirection: {xs: 'column', lg: 'row'},
          bgcolor: 'grey.100',
        }}
      >
        {renderNavVertical}
        <Main>{children}</Main>
      </Box>

      {/* Bottom nav mobile */}
      <BottomNav />
    </>
  );
}

AdminLayoutContent.propTypes = {
  children: PropTypes.node,
};

// Styles globaux pour le panel admin - Force le fond gris sur le body
const adminGlobalStyles = (
  <GlobalStyles
    styles={(theme) => ({
      body: {
        backgroundColor: theme.palette.grey[100],
      },
    })}
  />
);

export default function DashboardLayout({children}) {
  return (
    <AdminAccessProvider>
      {adminGlobalStyles}
      <AdminLayoutContent>{children}</AdminLayoutContent>
    </AdminAccessProvider>
  );
}

DashboardLayout.propTypes = {
  children: PropTypes.node,
};
